import * as React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentsIcon from "@mui/icons-material/Payments";
import PlaceIcon from "@mui/icons-material/Place";
import DescriptionIcon from "@mui/icons-material/Description";
import { formatDateWithTime, formatHour, gridSizeMdLg6 } from "@/shared";
import { TypographyFetch } from "@/erp/tickets-clientes/detalle-ticket/components/TypographyFetch";
import { getAgendamientos } from "@/shared/actions/erp";
import { Agendamiento } from "@/shared/interfaces";
import useModalInfoTrasladoLocalidadStore from "./useModalInfoTrasladoLocalidadStore";

/**
 * ModalInfoTrasladoLocalidad
 *
 * Muestra el RESUMEN completo de una orden de servicio de traslado:
 * datos de la orden, técnico/flota asignada, fecha/hora del traslado,
 * detalle de valores (recaudación / FibraPoints / diferido) y las
 * ubicaciones de origen (anterior) y destino (nueva).
 *
 * La orden completa llega por el store; la información de flota, fechas y
 * valores se resuelve consultando el Agendamiento de Django ligado por
 * `referencia_orden_servicio`.
 */

const SectionCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <Grid item xs={12}>
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "rgba(21,101,192,0.03)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {icon}
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1565c0" }}>
          {title}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  </Grid>
);

const Field: React.FC<{
  label: string;
  value?: React.ReactNode;
  sm?: number;
  breakWord?: boolean;
}> = ({ label, value, sm = 4, breakWord = false }) => (
  <Grid item xs={12} sm={sm}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={breakWord ? { wordBreak: "break-word" } : undefined}
    >
      {value === undefined || value === null || value === "" ? "—" : value}
    </Typography>
  </Grid>
);

const getEstadoColor = (
  estado?: string
): "warning" | "info" | "success" | "error" | "default" => {
  switch ((estado || "").toUpperCase()) {
    case "PENDIENTE":
      return "warning";
    case "EN PROCESO":
    case "EN_PROCESO":
    case "EN ESPERA":
    case "EN_ESPERA":
      return "info";
    case "REALIZADO":
    case "FINALIZADO":
      return "success";
    case "CANCELADO":
      return "error";
    default:
      return "default";
  }
};

export type GenericObjectModalProps = {
  title?: string;
};

export default function ModalInfoTrasladoLocalidad({
  title = "Detalles del traslado",
}: GenericObjectModalProps) {
  const {
    data,
    handleClose: onClose,
    open,
  } = useModalInfoTrasladoLocalidadStore();

  const [agendamiento, setAgendamiento] = useState<Agendamiento | null>(null);
  const [loading, setLoading] = useState(false);

  const traslado = data?.traslado;

  useEffect(() => {
    if (!open || !data?.id_orden_servicio) {
      setAgendamiento(null);
      return;
    }

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getAgendamientos({
          referencia_orden_servicio: data.id_orden_servicio,
          filterByState: false,
        } as any);
        if (!active) return;
        if (res?.code === 200 && (res.data?.count || 0) > 0) {
          setAgendamiento(res.data.data[0]);
        } else {
          setAgendamiento(null);
        }
      } catch {
        if (active) setAgendamiento(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, data?.id_orden_servicio]);

  // ----- Derivados desde el agendamiento (flota / fechas / valores) -----
  const obs = (agendamiento as any)?.observaciones || {};
  const rubros = obs.rubros_cobro || {};
  const ubicacionAnterior = obs.ubicacion_anterior || {};
  const flotaData = (agendamiento as any)?.flota_data;

  const esFibrapoints =
    rubros.puntos_canjeados != null &&
    `${rubros.puntos_canjeados}`.trim() !== "" &&
    Number(rubros.puntos_canjeados) > 0;

  const metodoRecaudacion = esFibrapoints
    ? "FibraPoints"
    : obs.recaudacion_tecnico
    ? "Recaudación técnico"
    : obs.cargo_a_factura
    ? "Cargo a factura"
    : "—";

  const valorTraslado =
    obs.valor_traslado != null && `${obs.valor_traslado}`.trim() !== ""
      ? `$${obs.valor_traslado}`
      : "—";

  const fechaTraslado = agendamiento?.fecha_instalacion || "—";
  const horaTraslado = agendamiento?.hora_instalacion
    ? formatHour(agendamiento.hora_instalacion)
    : "—";

  const tieneUbicacionAnterior =
    !!ubicacionAnterior?.coordenadas || !!ubicacionAnterior?.direccion;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* ===== Resumen de la orden ===== */}
          <SectionCard
            title="Resumen de la orden"
            icon={<DescriptionIcon fontSize="small" sx={{ color: "#1565c0" }} />}
          >
            <Field
              label="N° Orden"
              value={
                data?.numero_orden_servicio ||
                data?.id_orden_servicio?.substring(0, 8)
              }
            />
            <Field label="Tipo" value={data?.tipo_orden_servicio} />
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Estado
              </Typography>
              <Box>
                <Chip
                  label={data?.estado || "—"}
                  color={getEstadoColor(data?.estado)}
                  size="small"
                  sx={{ fontWeight: 500, height: 22 }}
                />
              </Box>
            </Grid>
            <Field
              label="Creado por"
              value={data?.user_create}
              sm={6}
            />
            <Field
              label="Fecha de creación"
              value={
                data?.fechaCreacion
                  ? formatDateWithTime(data.fechaCreacion)
                  : "—"
              }
              sm={6}
            />
          </SectionCard>

          {/* ===== Técnico / Flota + fechas del traslado ===== */}
          <SectionCard
            title="Técnico y agendamiento"
            icon={
              <LocalShippingIcon fontSize="small" sx={{ color: "#1565c0" }} />
            }
          >
            {loading ? (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Cargando agendamiento…
                  </Typography>
                </Box>
              </Grid>
            ) : agendamiento ? (
              <>
                <Field label="Flota / Cuadrilla" value={flotaData?.nombre} />
                <Field
                  label="Técnico líder"
                  value={flotaData?.lider_data?.razon_social}
                />
                <Field label="Auxiliar" value={flotaData?.auxiliar} />
                <Field
                  label="Teléfono flota"
                  value={flotaData?.telefono_1}
                />
                <Field label="Fecha del traslado" value={fechaTraslado} />
                <Field label="Hora del traslado" value={horaTraslado} />
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Estado agendamiento
                  </Typography>
                  <Box>
                    <Chip
                      label={agendamiento?.estado_agendamiento || "—"}
                      color={getEstadoColor(agendamiento?.estado_agendamiento)}
                      size="small"
                      sx={{ fontWeight: 500, height: 22 }}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Aún no hay agendamiento asociado a esta orden.
                </Typography>
              </Grid>
            )}
          </SectionCard>

          {/* ===== Detalle de valores / método de recaudación ===== */}
          {agendamiento && (
            <SectionCard
              title="Detalle de valores"
              icon={<PaymentsIcon fontSize="small" sx={{ color: "#1565c0" }} />}
            >
              <Field label="Método de recaudación" value={metodoRecaudacion} />
              <Field label="Valor traslado" value={valorTraslado} />
              {esFibrapoints && (
                <Field
                  label="FibraPoints antes / canjeados"
                  value={`${rubros.puntos_antes_canje ?? "—"} / ${
                    rubros.puntos_canjeados ?? "—"
                  }`}
                />
              )}
              {obs.cargo_a_factura && rubros.meses_pago != null && (
                <Field label="Meses a diferir" value={rubros.meses_pago} />
              )}
              {rubros.id_contrato_softnet && (
                <Field
                  label="Contrato Softnet"
                  value={rubros.id_contrato_softnet}
                />
              )}
            </SectionCard>
          )}

          {/* ===== Otros conceptos del traslado (equipos/servicios adicionales) ===== */}
          {agendamiento &&
            ((Array.isArray(obs.otros_conceptos) && obs.otros_conceptos.length > 0) ||
              (Array.isArray(obs.puntos_red) && obs.puntos_red.length > 0) ||
              (obs.excedente_fibra && typeof obs.excedente_fibra === "object")) && (
              <SectionCard
                title="Otros conceptos del traslado"
                icon={<PaymentsIcon fontSize="small" sx={{ color: "#1565c0" }} />}
              >
                {(obs.otros_conceptos || []).map((c: any, i: number) => (
                  <Field
                    key={`oc-${i}`}
                    label={c?.concepto || "Concepto"}
                    value={`${c?.tipo_pago || "—"}${
                      c?.tipo_pago === "FACTURA" && c?.meses_pago ? ` · ${c.meses_pago} mes(es)` : ""
                    } · $${Number(c?.valor_total || 0).toFixed(2)}${
                      Array.isArray(c?.series) && c.series.length ? ` · Serie ${c.series.join(", ")}` : ""
                    }`}
                  />
                ))}
                {(obs.puntos_red || []).map((p: any, i: number) => (
                  <Field
                    key={`pr-${i}`}
                    label={p?.nombre || `Punto de red ${i + 1}`}
                    value={`${Number(p?.metros || 0)} m (cobra ${Number(p?.metros_cobrados || 0)} m) · $${Number(
                      p?.valor_total || 0
                    ).toFixed(2)}${p?.concepto ? ` · ${p.concepto}` : ""}`}
                  />
                ))}
                {obs.excedente_fibra && typeof obs.excedente_fibra === "object" && (
                  <Field
                    label="Excedente de fibra"
                    value={`${Number(obs.excedente_fibra.metros || 0)} m (gratis ${Number(
                      obs.excedente_fibra.metros_gratis || 0
                    )} m, cobra ${Number(obs.excedente_fibra.metros_cobrados || 0)} m) · ${
                      obs.excedente_fibra.estado === "APROBADO"
                        ? `Aprobado · ${Number(obs.excedente_fibra.valor_total || 0).toFixed(2)}${
                            obs.excedente_fibra.tipo_pago ? ` · ${obs.excedente_fibra.tipo_pago}` : ""
                          }`
                        : obs.excedente_fibra.estado === "PENDIENTE"
                          ? "Pendiente de aprobación por Activaciones"
                          : "Sin cobro"
                    }`}
                  />
                )}
                {obs.conceptos_aprobacion?.estado && (
                  <Field
                    label="Aprobación Activaciones"
                    value={
                      obs.conceptos_aprobacion.estado === "APROBADO"
                        ? `Aprobado · ${obs.conceptos_aprobacion.aprobado_por || "—"}`
                        : `Pendiente · solicitado por ${obs.conceptos_aprobacion.solicitado_por || "—"}`
                    }
                  />
                )}
              </SectionCard>
            )}

          {/* ===== Ubicación de origen (anterior) ===== */}
          {tieneUbicacionAnterior && (
            <SectionCard
              title="Ubicación de origen (anterior)"
              icon={
                <PersonPinCircleIcon fontSize="small" sx={{ color: "#1565c0" }} />
              }
            >
              <Field
                label="Coordenadas"
                value={ubicacionAnterior?.coordenadas}
                sm={6}
              />
              <Field
                label="Dirección"
                value={ubicacionAnterior?.direccion}
                sm={6}
                breakWord
              />
            </SectionCard>
          )}

          {/* ===== Ubicación de destino (nueva) ===== */}
          <SectionCard
            title="Ubicación de destino (nueva)"
            icon={<PlaceIcon fontSize="small" sx={{ color: "#1565c0" }} />}
          >
            <Grid item {...gridSizeMdLg6}>
              <Typography variant="caption" color="text.secondary">
                Coordenadas
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {traslado?.coordenada || "—"}
              </Typography>
            </Grid>
            <Grid item {...gridSizeMdLg6}>
              <Typography variant="caption" color="text.secondary">
                Dirección
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ wordBreak: "break-word" }}
              >
                {traslado?.direccion || "—"}
              </Typography>
            </Grid>

            <TypographyFetch label="País" id={traslado?.pais || ""} entidad="pais" />
            <TypographyFetch
              label="Provincia"
              id={traslado?.provincia || ""}
              entidad="provincia"
            />
            <TypographyFetch
              label="Ciudad"
              id={traslado?.ciudad || ""}
              entidad="ciudad"
            />
            <TypographyFetch label="Zona" id={traslado?.zona || ""} entidad="zona" />
            <TypographyFetch
              label="Parroquia"
              id={traslado?.parroquia || ""}
              entidad="sector"
            />
          </SectionCard>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
